import React, { useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import TextArea from '@atlaskit/textarea'
import Select from '@atlaskit/select'
import { createReleaseAsync } from '../../store/slices/releaseSlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * CREATE RELEASE MODAL
 *
 * Modal for creating a new release.
 * Features:
 * - Release name and description
 * - Option to start empty or clone from existing release
 * - Clone functionality is placeholder (UI only)
 */
function CreateReleaseModal({ isOpen, onClose, projectId, existingReleases = [], onCloneRelease }) {
  const dispatch = useDispatch()
  const { showError, showWarning } = useNotification()

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [cloneOption, setCloneOption] = useState('empty') // 'empty' or 'clone'
  const [sourceRelease, setSourceRelease] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Portal target for Select dropdown - renders menu outside modal to avoid overflow clipping
  const portalTarget = useMemo(
    () => (typeof document !== 'undefined' ? document.body : null),
    []
  )
  // Select styles - zIndex 2000 ensures menu renders above modal (zIndex 1001)
  const selectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
      control: (base) => ({ ...base, fontSize: '13px' }),
    }),
    []
  )

  // Prepare release options for clone dropdown
  const releaseOptions = existingReleases
    .filter(r => !r.archived)
    .map(r => ({
      value: r.id,
      label: r.name
    }))

  // Handle form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      showWarning('Validation Error', 'Please enter a release name')
      return
    }

    // Validate clone option
    if (cloneOption === 'clone' && !sourceRelease) {
      showWarning('Validation Error', 'Please select a release to clone from')
      return
    }

    setIsSubmitting(true)

    try {
      if (cloneOption === 'clone' && sourceRelease) {
        // Delegate clone to parent (ReleaseList) which handles async polling
        onCloneRelease(sourceRelease.value, {
          name: name.trim(),
          description: description.trim()
        })
        // Close immediately — parent handles the async job + polling
        resetForm()
        onClose()
        return
      } else {
        // Create a new empty release
        await dispatch(createReleaseAsync({
          name: name.trim(),
          description: description.trim(),
          status: 'Active',
          jiraProjectId: projectId
        })).unwrap()
      }

      // Reset form and close modal
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to create release:', error)
      const errorMessage = error?.message || 'Failed to create release. Please try again.'
      showError('Create Release Failed', errorMessage)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Reset form state
  const resetForm = () => {
    setName('')
    setDescription('')
    setCloneOption('empty')
    setSourceRelease(null)
  }

  // Handle modal close
  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - pointer-events:none prevents click detection, Panel has pointer-events:auto */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '480px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)'
            }}
          >
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Create New Release
            </Dialog.Title>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            {/* Release Name */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Release Name *
              </label>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Release 2.1"
                isRequired
                autoFocus
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Description (optional)
              </label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Sprint 25 - Major feature release"
                minimumRows={2}
                maxHeight="100px"
              />
            </div>

            {/* Clone Options */}
            <div
              style={{
                backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                borderRadius: '8px',
                padding: '16px'
              }}
            >
              {/* Empty Option */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  cursor: 'pointer',
                  marginBottom: '12px'
                }}
              >
                <input
                  type="radio"
                  name="cloneOption"
                  value="empty"
                  checked={cloneOption === 'empty'}
                  onChange={() => setCloneOption('empty')}
                  style={{ marginTop: '2px' }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--ds-text, #172B4D)'
                  }}>
                    Start empty
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--ds-text-subtlest, #6B778C)',
                    marginTop: '2px'
                  }}>
                    No folders or scenarios
                  </div>
                </div>
              </label>

              {/* Clone Option - Select is placed outside label to prevent label click from closing the dropdown */}
              <div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="radio"
                    name="cloneOption"
                    value="clone"
                    checked={cloneOption === 'clone'}
                    onChange={() => setCloneOption('clone')}
                    style={{ marginTop: '2px' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--ds-text, #172B4D)'
                    }}>
                      Clone from existing release
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--ds-text-subtlest, #6B778C)',
                      marginTop: '2px'
                    }}>
                      Copies all folders and scenarios
                    </div>
                  </div>
                </label>

                {/* Source Release Selector - outside label to avoid label click interference */}
                {cloneOption === 'clone' && (
                  <div style={{ marginTop: '8px', marginLeft: '32px' }}>
                    <Select
                      options={releaseOptions}
                      value={sourceRelease}
                      onChange={setSourceRelease}
                      placeholder="Select source release..."
                      isDisabled={releaseOptions.length === 0}
                      menuPortalTarget={portalTarget}
                      menuPlacement="auto"
                      styles={selectStyles}
                    />
                    {releaseOptions.length === 0 && (
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--ds-text-warning, #FF8B00)',
                        marginTop: '8px'
                      }}>
                        No releases available to clone from
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!name.trim()}
            >
              Create Release
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { CreateReleaseModal }
